// Action types

import { Reducer, useCallback, useMemo, useRef } from 'react';

import { Middleware, useReducer } from '@spazious/react-hooks';

/**
 * Creates a store with a reducer and initial state
 */
export const useStore = <S, A>(
  reducer: Reducer<S, A>,
  initialState: S,
  middlewares: Middleware<S, A>[] = []
) => {
  const subscribeFn = useRef((action: A) => {
    listeners.current.forEach((listener) => listener(action));
  });
  const listeners = useRef(new Set<(action: A) => void>());
  const stateRef = useRef<S | undefined>();

  const subscribeMidleware = useCallback<Middleware<S, A>>(
    ({ getState }) =>
      (next) =>
      (action) => {
        stateRef.current = getState();
        subscribeFn.current?.(action);
        next(action);
      },
    []
  );

  const actualMiddlewares = useMemo(
    () => [subscribeMidleware, ...middlewares],
    [middlewares, subscribeMidleware]
  );

  const [state, dispatch] = useReducer(reducer, initialState, actualMiddlewares);

  const subscribe = useCallback((listener: (action: A) => void) => {
    if (listeners.current.has(listener)) {
      throw new Error(
        'Listener already exists check your code if your missing unsubscribe before subscribe'
      );
    }
    listeners.current.add(listener);
    return () => {
      listeners.current.delete(listener);
    };
  }, []);

  const getState = useCallback(() => stateRef.current!, []);

  const store = {
    getState,
    dispatch,
    subscribe,
    state,
  };

  return store;
};

export default useStore;
